import type { IAmountTick, ICancellationReasonByIdentitySummary, ICancellationReasonSummary, ICancellationUserInfo, ICancellationsSummary, IDashboardStatsSummary, IDateRange, IKpiSummary, ISalesByPaymentMethodSummary, ISalesForPaymentMethodSummary, ISalesSummary } from "@/data/model/Dashboard";
import { DashboardStatsPeriod } from "@/data/model/Enums";
import { DateTime } from "@/data/util/DateTime";



import { BaseServiceDao, parseCollection } from "./BaseServiceDao";


export class DashboardDao extends BaseServiceDao {
    public static className = "DashboardDao";

    constructor() {
        super("dashboard");
    }

    public getMonthlyTurnover(): Promise<Array<IAmountTick>> {
        return this.get("stats/monthlyturnover", undefined, (c) => parseCollection(c, DashboardDao.parseAmountTick));
    }

    public getMonthlySignups(): Promise<Array<IAmountTick>> {
        return this.get("stats/monthlysignup", undefined, (c) => parseCollection(c, DashboardDao.parseAmountTick));
    }

    public getDailyAllocations(): Promise<Array<IAmountTick>> {
        return this.get("stats/dailyallocation", undefined, (c) => parseCollection(c, DashboardDao.parseAmountTick));
    }

    public getSalesSummary(
        locationId: string,
        period: DashboardStatsPeriod,
        selectedDate: Date | undefined
    ): Promise<ISalesSummary> {
        return this.post(
            `stats/${locationId}/sales/${DashboardStatsPeriod[period]}`,
            { selectedDate: selectedDate },
            DashboardDao.parseSalesSummary
        );
    }

    public getSalesByPaymentMethodSummary(
        locationId: string,
        period: DashboardStatsPeriod,
        selectedDate: Date | undefined
    ): Promise<ISalesByPaymentMethodSummary> {
        return this.post(
            `stats/${locationId}/sales-by-payment-method/${DashboardStatsPeriod[period]}`,
            { selectedDate: selectedDate },
            DashboardDao.parseSalesByPaymentMethodSummary
        );
    }

    public getCancellationsSummary(
        locationId: string,
        period: DashboardStatsPeriod,
        selectedDate: Date | undefined
    ): Promise<ICancellationsSummary> {
        return this.post(
            `stats/${locationId}/cancellations/${DashboardStatsPeriod[period]}`,
            { selectedDate: selectedDate },
            DashboardDao.parseCancellationsSummary
        );
    }

    public getKpiSummary(
        locationId: string,
        period: DashboardStatsPeriod,
        selectedDate: Date | undefined
    ): Promise<IKpiSummary> {
        return this.post(
            `stats/${locationId}/kpi/${DashboardStatsPeriod[period]}`,
            { selectedDate: selectedDate },
            DashboardDao.parseKpiSummary
        );
    }

    public static parseSalesSummary(literal: any): ISalesSummary {
        return {
            ...DashboardDao.parseStatsSummary(literal),
            totalAmount: parseFloat(literal.totalAmount),
            orders: parseInt(literal.orders),
            payments: parseInt(literal.payments),
        };
    }

    public static parseSalesByPaymentMethodSummary(literal: any): ISalesByPaymentMethodSummary {
        return {
            dateRange: DashboardDao.parseDateRange(literal.dateRange),
            onlineBrowserPaymentMethodSummaries: parseCollection(literal.onlineBrowserPaymentMethodSummaries, (c) =>
                DashboardDao.parseSalesForPaymentMethodSummary(c)
            ),
            onlinePaymentDevicePaymentMethodSummaries: parseCollection(
                literal.onlinePaymentDevicePaymentMethodSummaries,
                (c) => DashboardDao.parseSalesForPaymentMethodSummary(c)
            ),
            offlinePaymentMethodSummaries: parseCollection(literal.offlinePaymentMethodSummaries, (c) =>
                DashboardDao.parseSalesForPaymentMethodSummary(c)
            ),
            nrePaymentMethodSummaries: parseCollection(literal.nrePaymentMethodSummaries, (c) =>
                DashboardDao.parseSalesForPaymentMethodSummary(c)
            ),
        };
    }

    public static parseSalesForPaymentMethodSummary(literal: any): ISalesForPaymentMethodSummary {
        return {
            paymentMethodName: literal.paymentMethodName,
            totalAmount: parseFloat(literal.totalAmount),
        };
    }

    public static parseCancellationsSummary(literal: any): ICancellationsSummary {
        return {
            dateRange: DashboardDao.parseDateRange(literal.dateRange),
            cancellationReasonSummaries: parseCollection(literal.cancellationReasonSummaries, (c) =>
                DashboardDao.parseCancellationReasonSummary(c)
            ),
            cancellationUsers: parseCollection(literal.cancellationUsers, (c) =>
                DashboardDao.parseCancellationUserInfo(c)
            ),
        };
    }

    public static parseCancellationReasonSummary(literal: any): ICancellationReasonSummary {
        return {
            reason: literal.reason != null ? parseInt(literal.reason) : undefined,
            amount: parseFloat(literal.amount),
            summariesByIdentity: parseCollection(literal.summariesByIdentity, (c) =>
                DashboardDao.parseCancellationSummaryByIdentity(c)
            ),
        };
    }

    public static parseCancellationSummaryByIdentity(literal: any): ICancellationReasonByIdentitySummary {
        return {
            identityId: literal.identityId,
            userName: literal.userName,
            amount: parseFloat(literal.amount),
        };
    }

    public static parseCancellationUserInfo(literal: any): ICancellationUserInfo {
        return {
            identityId: literal.identityId,
            userName: literal.userName,
            amount: parseFloat(literal.amount),
        };
    }

    public static parseKpiSummary(literal: any): IKpiSummary {
        return {
            netTip: parseFloat(literal.netTip),
            netSalesNetExclTip: parseFloat(literal.netSalesNetExclTip),
            grossSalesNetExclTip: parseFloat(literal.grossSalesNetExclTip),
            avgOrder: literal.avgOrder != null ? parseFloat(literal.avgOrder) : undefined,
            avgPayment: literal.avgPayment != null ? parseFloat(literal.avgPayment) : undefined,
        };
    }

    public static parseStatsSummary(literal: any): IDashboardStatsSummary {
        return {
            granularity: parseInt(literal.granularity),
            dateRanges: parseCollection(literal.dateRanges, (c) => DashboardDao.parseDateRange(c)),
            data: parseCollection(literal.data, (c) => DashboardDao.parseAmountTick(c)),
        };
    }

    public static parseAmountTick(literal: any): IAmountTick {
        return {
            amount: parseFloat(literal.amount),
            dateRange: DashboardDao.parseDateRange(literal.dateRange),
        };
    }

    public static parseDateRange(literal: any): IDateRange {
        return {
            from: DateTime.parseIsoDate(literal.from),
            to: DateTime.parseIsoDate(literal.to),
        };
    }
}
